import isChineseText from './isChineseText.js';

/**
 * Creates a Chinese helper function for matching words in sentences
 * This is used to provide consistent Chinese text processing across all functions
 *
 * @returns {Function} Chinese helper function for matching words
 */
export function createChineseMatchHelper() {
  return function(sentence, word) {
    const text = typeof sentence === 'string' ? sentence : sentence.text || sentence;
    const matches = [];

    if (!text || !word) {
      return matches;
    }

    const lowerText = text.toLowerCase();
    const lowerWord = word.toLowerCase();

    let startIndex = 0;
    let index;

    // Use exact string matching for Chinese text
    while ((index = lowerText.indexOf(lowerWord, startIndex)) !== -1) {
      matches.push(word);
      startIndex = index + lowerWord.length;
    }

    return matches;
  };
}

/**
 * Creates an enhanced Chinese helper function specifically for URL/slug matching
 * This provides better matching logic for Chinese keywords in URL contexts
 *
 * @returns {Function} Enhanced Chinese helper function for URL matching
 */
export function createChineseUrlMatchHelper() {
  return function(slugText, chineseChar) {
    const text = typeof slugText === 'string' ? slugText : slugText.text || slugText;
    const matches = [];

    if (!text || !chineseChar) {
      return matches;
    }

    const lowerText = text.toLowerCase();
    const lowerChar = chineseChar.toLowerCase();

    // First, try exact character matching (for Chinese characters in slug)
    let startIndex = 0;
    let index;
    while ((index = lowerText.indexOf(lowerChar, startIndex)) !== -1) {
      matches.push(chineseChar);
      startIndex = index + lowerChar.length;
    }

    // If no exact matches found and we have a Chinese character, try fuzzy matching
    if (matches.length === 0 && isChineseText(chineseChar)) {
      // For URL context, be more lenient - if the slug contains any related terms,
      // we'll consider it a partial match to improve user experience
      const hasRelevantContent = checkForRelevantContent(lowerText, lowerChar);
      if (hasRelevantContent) {
        matches.push(chineseChar);
      }
    }

    return matches;
  };
}

/**
 * Checks if the slug contains content that might be related to the Chinese character
 * This is a fuzzy matching approach for better URL keyword assessment
 *
 * @param {string} slugText - The slug text to check
 * @param {string} chineseChar - The Chinese character to match
 * @returns {boolean} True if relevant content is found
 */
function checkForRelevantContent(slugText, chineseChar) {
  // For common Chinese business/logistics terms, provide some fuzzy matching
  const chineseToEnglishMap = {
    跨: ['cross', 'trans', 'inter', 'kuà', 'kua', 'kuajing'],
    境: ['border', 'boundary', 'realm', 'jìng', 'jing', 'kuajing'],
    物: ['goods', 'item', 'thing', 'material', 'wù', 'wu', 'wuliu'],
    流: ['flow', 'current', 'logistics', 'liú', 'liu', 'wuliu'],
    货: ['goods', 'cargo', 'freight', 'huò', 'huo'],
    运: ['transport', 'shipping', 'yùn', 'yun'],
    输: ['transport', 'transmit', 'shū', 'shu'],
    送: ['deliver', 'send', 'sòng', 'song'],
    配: ['distribute', 'match', 'pèi', 'pei'],
    仓: ['warehouse', 'storage', 'cāng', 'cang'],
    储: ['store', 'storage', 'chǔ', 'chu']
  };

  const possibleMatches = chineseToEnglishMap[chineseChar] || [];

  // Check if slug contains any related terms
  return possibleMatches.some(term => slugText.includes(term.toLowerCase()));
}

/**
 * Gets appropriate match helper based on text content
 * Auto-detects Chinese text and returns Chinese helper if needed
 *
 * @param {string} text - Text to analyze
 * @param {string} keyword - Keyword to analyze
 * @param {Function} existingHelper - Existing helper if available
 * @param {boolean} isUrlContext - Whether this is for URL/slug matching
 * @returns {Function|boolean} Appropriate helper function or false
 */
export function getMatchHelper(text, keyword, existingHelper, isUrlContext = false) {
  // If there's already a custom helper, use it
  if (existingHelper) {
    return existingHelper;
  }

  // Auto-detect Chinese and provide appropriate Chinese helper
  if (isChineseText(keyword + ' ' + text)) {
    return isUrlContext ? createChineseUrlMatchHelper() : createChineseMatchHelper();
  }

  // Return false for default behavior with other languages
  return false;
}

/**
 * Applies Chinese helper to researcher if Chinese text is detected
 * This is a utility function for updating researchers dynamically
 *
 * @param {Object} researcher - The researcher object to update
 * @param {string} text - Text to analyze
 * @param {string} keyword - Keyword to analyze
 * @param {boolean} isUrlContext - Whether this is for URL/slug matching
 * @returns {Object} Updated researcher object
 */
export function enhanceResearcherForChinese(researcher, text, keyword, isUrlContext = false) {
  if (!researcher.getHelper('matchWordCustomHelper') && isChineseText(keyword + ' ' + text)) {
    // Clone the researcher to avoid modifying original
    const enhancedResearcher = Object.create(researcher);
    enhancedResearcher.helpers = {...researcher.helpers};
    enhancedResearcher.helpers.matchWordCustomHelper = isUrlContext
      ? createChineseUrlMatchHelper()
      : createChineseMatchHelper();
    return enhancedResearcher;
  }

  return researcher;
}

/**
 * Checks if auto-Chinese enhancement should be applied
 *
 * @param {Object} researcher - The researcher object
 * @param {string} text - Text to analyze
 * @param {string} keyword - Keyword to analyze
 * @returns {boolean} True if Chinese enhancement should be applied
 */
export function shouldEnhanceForChinese(researcher, text, keyword) {
  return !researcher.getHelper('matchWordCustomHelper') && isChineseText(keyword + ' ' + text);
}

export default {
  createChineseMatchHelper,
  createChineseUrlMatchHelper,
  getMatchHelper,
  enhanceResearcherForChinese,
  shouldEnhanceForChinese
};
